import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { socketChatTotalUnreadCount } from 'src/actions/chatActions';
import { setActivityCounts } from 'src/actions/activityActions';
import { addNotification } from 'src/actions/notificationActions';
import {
  NOTIFICATION_STATUS,
  SOCKET,
  PERMISSIONS,
} from 'src/constants';
import helpers from 'src/helpers';

const { SOCKET_EVENTS, SOCKET_CLIENT_EVENTS } = SOCKET;
const { PERMISSIONS_SYSTEM_CODE } = PERMISSIONS;

const WebSocketContext = createContext(null);

export function WebSocketProvider({ children }) {
  const [isConnected, setConnected] = useState(false);
  const dispatch = useDispatch();
  const socket = useRef(null);

  const {
    id,
    userRole,
  } = useSelector((state) => state?.account?.user || {});

  const { permissions: userRolePermissions } = userRole || {};

  const token = localStorage.getItem('accessToken');

  const handleChatTotalUnreadCount = (message) => {
    dispatch(socketChatTotalUnreadCount(message));
    socket.current.off(SOCKET_EVENTS.CHAT_TOTAL_UNREAD_COUNT, handleChatTotalUnreadCount);
  };

  // CON-XXXX — activity badges. Kept in Redux for the sidebar badge, the
  // Smart Forms list and the Entries tab.
  const handleActivityCounts = (payload) => {
    dispatch(setActivityCounts(payload));
  };

  const socketErrorMessage = (message) => {
    dispatch(addNotification('SOCKET_ERROR', message, NOTIFICATION_STATUS.ERROR));
  };

  const handleSocketError = () => {
    socketErrorMessage('Wrong connection. Please reload this page.');
  };

  useEffect(() => {
    if (
      !isConnected
      && token
      && id
      // TODO: We should remove the next lines when all users use sockets. It will not make sense in the future.
      // TODO: Don't forget to update BE side
      && helpers.permissions.doesUserHaveOneOfPermissions([
        PERMISSIONS_SYSTEM_CODE.CHAT_VIEW,
        PERMISSIONS_SYSTEM_CODE.DAILY_TEXT_USAGE_SIDEBAR,
      ], userRolePermissions)
    ) {
      socket.current = io(process.env.REACT_APP_API_BASE_URL, {
        transports: ['websocket', 'webtransport'],
        reconnection: true,
        reconnectionAttempts: 3,
        auth: { token },
      });

      // CON-XXXX — only ACTIVITY_BADGES holders ask for activity counts
      // (on every connect and reconnect), so nobody else costs the server
      // anything.
      const hasActivityBadges = helpers.permissions.doesUserHavePermission(
        PERMISSIONS_SYSTEM_CODE.ACTIVITY_BADGES,
        userRolePermissions,
      );

      socket.current.on('connect', () => {
        setConnected(true);
        if (hasActivityBadges) {
          socket.current.emit(SOCKET_CLIENT_EVENTS.ACTIVITY_COUNTS_REQUEST);
        }
      });

      socket.current.on('connect_error', handleSocketError);
      socket.current.on('connect_timeout', handleSocketError);
      socket.current.on('reconnect_error', handleSocketError);

      // We need it because BE side sends this event on socket init
      socket.current.on(SOCKET_EVENTS.CHAT_TOTAL_UNREAD_COUNT, handleChatTotalUnreadCount);

      if (hasActivityBadges) {
        socket.current.on(SOCKET_EVENTS.ACTIVITY_COUNTS, handleActivityCounts);
      }
    }

    return () => {
      if (socket.current) {
        socket.current.close();
        setConnected(false);
      }
    };
  }, [id]);

  return (
    <WebSocketContext.Provider value={socket.current}>
      {children}
    </WebSocketContext.Provider>
  );
}

WebSocketProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useWebSocket = () => useContext(WebSocketContext);
